import type { AppError, Result } from '@shared/utils';
import { err, ok, getEnvironment } from '@shared/utils';
import type { EnvironmentConfig } from '../config/interfaces/EnvironmentConfig';
import type { PackageResourceConfig } from '../config/types/PackageResourceConfig';
import type { Environment } from '@shared/utils';
import { CORE_ERROR_CODES } from '../constants/CORE_ERROR_CODES';
import { CORE_MESSAGES } from '../constants/CORE_MESSAGES';

/**
 * @group Core Infrastructure
 * @summary
 * <blockquote>Holds per-environment configuration for a package and resolves the active
 * environment config at runtime.</blockquote>
 *
 * @remarks
 * Each shared package that needs environment-specific behaviour (e.g. Logger)
 * extends this class. The config is set during bootstrap via `setConfig` and
 * later resolved with `resolveExecutionPlan`, which detects the current
 * environment and returns the matching `EnvironmentConfig`.
 *
 * @example
 * ```typescript
 * import { BaseRuntimeManager } from '@shared/core';
 *
 * class LoggerRuntimeManager extends BaseRuntimeManager<LoggerEnvironmentConfig> {}
 *
 * const manager = new LoggerRuntimeManager();
 * manager.setConfig(loggerResourceConfig);
 *
 * const plan = manager.resolveExecutionPlan();
 * plan.match({
 *   ok: ({ envConfig, environment }) => { /* use envConfig.adapters *\/ },
 *   err: (error) => { /* handle missing config *\/ },
 * });
 * ```
 */
export class BaseRuntimeManager<TEnvConfig extends EnvironmentConfig<any>> {
  private config: PackageResourceConfig<TEnvConfig> | null = null;

  setConfig(config: PackageResourceConfig<TEnvConfig>): void {
    this.config = config;
  }

  resolveExecutionPlan(): Result<{ envConfig: TEnvConfig; environment: Environment }, AppError> {
    const envResult = getEnvironment();
    if (envResult.isErr) {
      return err(envResult.error);
    }

    if (!this.config) {
      return err({
        errorCode: CORE_ERROR_CODES.CONFIG_NOT_SET,
        errorMessage: CORE_MESSAGES.CONFIG_NOT_SET,
      });
    }

    const envConfig = this.config[envResult.value];
    if (!envConfig) {
      return err({
        errorCode: CORE_ERROR_CODES.ENV_CONFIG_MISSING,
        errorMessage: CORE_MESSAGES.ENV_CONFIG_MISSING.replace('{environment}', envResult.value),
      });
    }

    return ok({ envConfig, environment: envResult.value });
  }
}
